import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

/** Standard product-form POST with Shopify's documented return_to redirect. */
class HvBuyNow extends HTMLElement {
  #controller;
  #observer;
  #pending = false;
  #submitting = false;
  #generation = 0;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const component = this.closest('product-form-component');
    const scope = component?.closest('.shopify-section, dialog, product-card');
    if (!component) return;
    this.hidden = false;
    this.addEventListener('click', this.#buy, { signal });
    component.addEventListener('input',this.#sync,{signal});
    component.addEventListener('change',this.#sync,{signal});
    component.addEventListener(ThemeEvents.quantitySelectorUpdate,this.#sync,{signal});
    scope?.addEventListener(StandardEvents.productSelect, async (event) => {
      if (event.target.closest('product-card') && !event.target.closest('product-card').contains(this)) return;
      const generation = ++this.#generation;
      this.#pending = true;
      this.#sync();
      try {
        await event.promise;
      } catch {
        // Native product form owns variant errors and availability.
      } finally {
        if (generation === this.#generation && !signal.aborted) {
          this.#pending = false;
          queueMicrotask(() => this.#sync());
        }
      }
    }, { signal, capture: true });
    window.addEventListener('pageshow', () => {
      this.#submitting = false;
      this.#sync();
    }, { signal });
    this.#observer = new MutationObserver(() => this.#sync());
    this.#observer.observe(component, { subtree: true, attributes: true, attributeFilter: ['disabled', 'value'] });
    this.#sync();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  #sync = () => {
    const button = this.querySelector('[data-hv-buy-now]');
    const component = this.closest('product-form-component');
    const addButton = component?.querySelector('[ref="addToCartButton"]');
    const variant = component?.querySelector('[name="id"]');
    const disabled = this.#pending || this.#submitting || !variant?.value || !addButton || addButton.disabled;
    if (button && button.disabled !== disabled) button.disabled = disabled;
    const shopPay = this.parentElement?.querySelector('[data-hv-shop-pay]');
    const shopButton = shopPay?.querySelector('[data-hv-shop-pay-link]');
    const form = component?.querySelector('form');
    if (shopPay && shopButton && form) {
      const data = new FormData(form);
      const quantityInput = form.querySelector('input[name="quantity"]');
      const quantity = Number(data.get('quantity') || 1);
      // This explicit Shop Pay permalink carries the selected variant + quantity only.
      const hasExtraData = [...data.entries()].some(([name,value]) =>
        (name === 'selling_plan' || name.startsWith('properties[')) &&
        (typeof value === 'string' ? value.trim() !== '' : value.size > 0));
      const invalidQuantity = !Number.isInteger(quantity) || quantity < 1 || (quantityInput && !quantityInput.checkValidity());
      const blocked = disabled || invalidQuantity || !form.checkValidity();
      shopPay.hidden = hasExtraData;
      shopPay.inert = blocked || hasExtraData;
      shopPay.setAttribute('aria-disabled', String(blocked || hasExtraData));
      shopButton.setAttribute('aria-disabled',String(blocked || hasExtraData));
      shopButton.tabIndex = blocked || hasExtraData ? -1 : 0;
      if (!blocked && !hasExtraData) {
        const url = new URL(`${shopButton.dataset.cartUrl}/${variant.value}:${quantity}`,location.origin);
        url.searchParams.set('payment','shop_pay');
        shopButton.href = url.href;
      } else shopButton.removeAttribute('href');
    }
  };

  #buy = (event) => {
    if (!event.target.closest('[data-hv-buy-now]')) return;
    const component = this.closest('product-form-component');
    const form = component?.querySelector('form');
    this.#sync();
    if (!form || this.querySelector('[data-hv-buy-now]')?.disabled || !form.reportValidity()) return;
    const quantity = component.refs?.quantitySelector?.canAddToCart?.();
    if (quantity && !quantity.canAdd) return;
    let redirect = form.querySelector('input[name="return_to"]');
    if (!redirect) {
      redirect = document.createElement('input');
      redirect.type = 'hidden';
      redirect.name = 'return_to';
      form.append(redirect);
    }
    redirect.value = this.dataset.checkoutUrl;
    this.#submitting = true;
    this.#sync();
    // Submit the actual form: preserve quantity, selling plan, properties and uploaded files.
    // Bypass only the theme's AJAX add-to-cart drawer handler for this explicit checkout action.
    HTMLFormElement.prototype.submit.call(form);
  };
}

if (!customElements.get('hv-buy-now')) customElements.define('hv-buy-now', HvBuyNow);
